/**
 * Create / edit form for Accounting V2 expenses.
 *
 * This is the entry point into the V2 expense engine. Without it nothing can
 * reach `acc_expenses`, which is why every downstream tab (Approvals, Refunds,
 * Billable) had nothing to show.
 *
 * The live totals panel mirrors the server's computeExpenseTax so the effect of
 * tax treatment, discount and withholding is visible before saving. The server
 * recomputes everything on save and its figures are authoritative.
 */
import { useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import toast from 'react-hot-toast';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Camera, Upload as UploadIcon } from 'lucide-react';
import {
  useCreateExpenseV2,
  useUpdateExpenseV2,
  useExpenseCategories,
  type Expense,
  type CreateExpensePayload,
  type ExpenseSettlementMode,
  type ExpenseTaxTreatment,
} from '../hooks/useExpenseApi';
import { formatMinor, toMinorUnits, toMajorInput, previewExpenseTax } from '../utils/money';
import { localToday } from '@/shared/utils/utils';

const SETTLEMENT_MODES: Array<{ value: ExpenseSettlementMode; label: string; hint: string }> = [
  { value: 'paid_cash', label: 'Paid by cash', hint: 'Dr Expense · Cr Cash' },
  { value: 'paid_bank', label: 'Paid from bank', hint: 'Dr Expense · Cr Bank' },
  { value: 'corporate_card', label: 'Corporate card', hint: 'Dr Expense · Cr Card Clearing' },
  { value: 'employee_paid', label: 'Employee paid personally', hint: 'Dr Expense · Cr Employee Payable' },
  { value: 'supplier_credit', label: 'Unpaid supplier invoice', hint: 'Dr Expense · Cr Accounts Payable' },
];

const TAX_TREATMENTS: Array<{ value: ExpenseTaxTreatment; label: string; hint: string }> = [
  { value: 'exclusive', label: 'Tax exclusive', hint: 'Tax is added on top of the amount entered.' },
  { value: 'inclusive', label: 'Tax inclusive', hint: 'Tax is extracted from the amount entered.' },
  { value: 'zero_rated', label: 'Zero rated', hint: 'No tax, and none reclaimable.' },
  { value: 'exempt', label: 'Exempt', hint: 'No tax, and none reclaimable.' },
  { value: 'reverse_charge', label: 'Reverse charge', hint: 'You self-assess the tax; the supplier is paid net.' },
];

/** Settlement modes whose credit account is tracked per contact. */
const REQUIRES_CONTACT: Partial<Record<ExpenseSettlementMode, 'supplier' | 'employee'>> = {
  supplier_credit: 'supplier',
  employee_paid: 'employee',
};

interface FormState {
  expenseDate: string;
  amount: string;
  discount: string;
  taxRate: string;
  taxTreatment: ExpenseTaxTreatment;
  withholdingRate: string;
  currency: string;
  categoryId: string;
  description: string;
  referenceNumber: string;
  notes: string;
  settlementMode: ExpenseSettlementMode;
  supplierId: string;
  supplierName: string;
  employeeId: string;
  employeeName: string;
  departmentId: string;
  costCenterId: string;
  campaignId: string;
  isBillable: boolean;
  isReimbursable: boolean;
  markupPercent: string;
  receiptUrl: string;
}

function emptyForm(): FormState {
  return {
    expenseDate: localToday(),
    amount: '',
    discount: '',
    taxRate: '18',
    taxTreatment: 'exclusive',
    withholdingRate: '',
    currency: 'INR',
    categoryId: '',
    description: '',
    referenceNumber: '',
    notes: '',
    settlementMode: 'paid_cash',
    supplierId: '',
    supplierName: '',
    employeeId: '',
    employeeName: '',
    departmentId: '',
    costCenterId: '',
    campaignId: '',
    isBillable: false,
    isReimbursable: false,
    markupPercent: '',
    receiptUrl: '',
  };
}

function fromExpense(e: Expense): FormState {
  return {
    ...emptyForm(),
    expenseDate: e.expenseDate ? new Date(e.expenseDate).toISOString().slice(0, 10) : '',
    amount: toMajorInput(e.taxTreatment === 'inclusive' ? e.grossAmountMinor : e.netAmountMinor),
    discount: toMajorInput(e.discountMinor),
    taxRate: String(e.taxRate ?? 0),
    taxTreatment: e.taxTreatment,
    currency: e.currency || 'INR',
    categoryId: e.categoryId || '',
    description: e.description || '',
    referenceNumber: e.referenceNumber || '',
    notes: e.notes || '',
    settlementMode: e.settlementMode,
    supplierId: e.supplierId || '',
    supplierName: e.supplierName || '',
    employeeId: e.employeeId || '',
    employeeName: e.employeeName || '',
    departmentId: e.departmentId || '',
    costCenterId: e.costCenterId || '',
    campaignId: e.campaignId || '',
    isBillable: !!e.isBillable,
    isReimbursable: !!e.isReimbursable,
    markupPercent: e.markupPercent ? String(e.markupPercent) : '',
    receiptUrl: e.attachments?.[0]?.fileUrl ?? '',
  };
}

export interface ExpenseFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Supplied when editing, or as the source when duplicating. */
  expense?: Expense | null;
  /**
   * 'edit' updates the supplied expense. 'duplicate' seeds the form from it but
   * creates a new record. Defaults to 'edit' when an expense is given.
   */
  mode?: 'create' | 'edit' | 'duplicate';
}

export function ExpenseFormModal({ open, onClose, expense, mode }: ExpenseFormModalProps) {
  // State is seeded once from props. The caller mounts this component only while
  // the modal is open and gives it a key, so each open starts from a clean slate
  // without resetting state inside an effect.
  const [form, setForm] = useState<FormState>(() => (expense ? fromExpense(expense) : emptyForm()));

  const create = useCreateExpenseV2();
  const update = useUpdateExpenseV2();
  const categories = useExpenseCategories({ isActive: true });

  // Proof-of-expense photo upload (client-requested: field staff — drivers,
  // field-visit employees — need to attach a photo of the receipt, not type
  // in a URL to one already hosted somewhere else, which is what this field
  // required before). Same generic /api/upload endpoint every other file
  // upload in the app already uses (see salesdocs/hooks/useSalesDocs.ts's
  // useFileUpload — not imported directly to avoid a cross-feature
  // dependency on an unrelated module for three lines of logic).
  const receiptFileRef = useRef<HTMLInputElement>(null);
  const uploadReceipt = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post<{ url: string }>('/api/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.url;
    },
  });
  const onReceiptFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    uploadReceipt.mutate(file, {
      onSuccess: (url) => {
        set('receiptUrl', url);
        toast.success('Receipt photo uploaded.');
      },
      onError: () => toast.error('Receipt upload failed.'),
    });
  };

  // Duplicating seeds from an existing expense but must still CREATE, so the
  // edit path is keyed off the resolved mode rather than merely having a source.
  const resolvedMode = mode ?? (expense ? 'edit' : 'create');
  const isEdit = resolvedMode === 'edit' && !!expense;
  const busy = create.isPending || update.isPending;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  /**
   * Selecting a category pulls in its configured tax defaults and flags, which
   * is the "smart behaviour" the specification asks for.
   */
  const onCategoryChange = (categoryId: string) => {
    const category = (categories.data?.items ?? []).find((c) => c._id === categoryId);
    setForm((prev) => ({
      ...prev,
      categoryId,
      taxRate: category ? String(category.defaultTaxRate ?? prev.taxRate) : prev.taxRate,
      taxTreatment: category?.defaultTaxTreatment ?? prev.taxTreatment,
      isBillable: category ? !!category.isBillableByDefault : prev.isBillable,
      isReimbursable: category ? !!category.isReimbursableByDefault : prev.isReimbursable,
    }));
  };

  const totals = useMemo(
    () =>
      previewExpenseTax({
        netMinor: toMinorUnits(form.amount),
        discountMinor: toMinorUnits(form.discount),
        taxRate: Number(form.taxRate) || 0,
        treatment: form.taxTreatment,
        withholdingRate: Number(form.withholdingRate) || 0,
      }),
    [form.amount, form.discount, form.taxRate, form.taxTreatment, form.withholdingRate],
  );

  const contactRequirement = REQUIRES_CONTACT[form.settlementMode];

  const selectedCategory = useMemo(
    () => (categories.data?.items ?? []).find((c) => c._id === form.categoryId),
    [categories.data?.items, form.categoryId],
  );

  /** Client-side mirror of the server's rules, for immediate feedback only. */
  const validationError = useMemo(() => {
    if (!form.expenseDate) return 'An expense date is required.';
    if (toMinorUnits(form.amount) <= 0) return 'Enter an amount greater than zero.';
    if (toMinorUnits(form.discount) > toMinorUnits(form.amount)) return 'Discount cannot exceed the amount.';
    if (contactRequirement === 'supplier' && !form.supplierId.trim()) {
      return 'An unpaid supplier invoice needs a supplier ID, because it posts to Accounts Payable.';
    }
    if (contactRequirement === 'employee' && !form.employeeId.trim()) {
      return 'An employee-paid expense needs an employee ID, because it posts to Employee Payable.';
    }
    if (selectedCategory?.requiresReceipt && !form.receiptUrl.trim() && !form.referenceNumber.trim()) {
      return `Category '${selectedCategory.name}' requires a receipt attachment URL or reference number.`;
    }
    return null;
  }, [form, contactRequirement, selectedCategory]);

  const submit = (alsoSubmitForApproval: boolean) => {
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const attachments = form.receiptUrl.trim()
      ? [{ kind: 'receipt' as const, fileName: 'Receipt', fileUrl: form.receiptUrl.trim() }]
      : (expense?.attachments ?? []);

    const payload: CreateExpensePayload = {
      expenseDate: form.expenseDate,
      netAmountMinor: toMinorUnits(form.amount),
      discountMinor: toMinorUnits(form.discount),
      taxRate: Number(form.taxRate) || 0,
      taxTreatment: form.taxTreatment,
      withholdingRate: Number(form.withholdingRate) || 0,
      currency: form.currency || 'INR',
      categoryId: form.categoryId || undefined,
      description: form.description,
      referenceNumber: form.referenceNumber,
      notes: form.notes,
      settlementMode: form.settlementMode,
      supplierId: form.supplierId || undefined,
      supplierName: form.supplierName || undefined,
      employeeId: form.employeeId || undefined,
      employeeName: form.employeeName || undefined,
      departmentId: form.departmentId || undefined,
      costCenterId: form.costCenterId || undefined,
      campaignId: form.campaignId || undefined,
      isBillable: form.isBillable,
      isReimbursable: form.isReimbursable,
      markupPercent: Number(form.markupPercent) || 0,
      attachments,
      submit: alsoSubmitForApproval,
    };

    const onSuccess = () => {
      toast.success(
        isEdit ? 'Expense updated'
          : alsoSubmitForApproval ? 'Expense created and submitted for approval'
            : 'Expense saved as draft',
      );
      onClose();
    };
    const onError = (e: unknown) => toast.error(getApiErrorMessage(e) || 'Could not save the expense');

    if (isEdit && expense) update.mutate({ id: expense._id, payload }, { onSuccess, onError });
    else create.mutate(payload, { onSuccess, onError });
  };

  const categoryOptions = [
    { value: '', label: 'Uncategorised' },
    ...(categories.data?.items ?? []).map((c) => ({ value: c._id, label: `${c.name} (${c.code})` })),
  ];

  const selectedTreatment = TAX_TREATMENTS.find((t) => t.value === form.taxTreatment);
  const selectedSettlement = SETTLEMENT_MODES.find((s) => s.value === form.settlementMode);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        isEdit ? `Edit ${expense?.expenseNo}`
          : resolvedMode === 'duplicate' ? `New expense (copy of ${expense?.expenseNo})`
            : 'New expense'
      }
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-slate-500">
            {validationError ?? `Total ${formatMinor(totals.grossMinor, form.currency)}`}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="secondary" onClick={() => submit(false)} disabled={busy || !!validationError}>
              {isEdit ? 'Save' : 'Save draft'}
            </Button>
            {!isEdit ? (
              <Button onClick={() => submit(true)} disabled={busy || !!validationError}>
                Save &amp; submit
              </Button>
            ) : null}
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* General */}
        <section className="grid gap-3 sm:grid-cols-2">
          <Input
            type="date"
            label="Expense date"
            value={form.expenseDate}
            onChange={(e) => set('expenseDate', e.target.value)}
            required
          />
          <div>
            <Select
              label="Category"
              value={form.categoryId}
              onChange={(e) => onCategoryChange(e.target.value)}
              options={categoryOptions}
            />
            {!categories.data?.items?.length ? (
              <p className="mt-1 text-xs text-slate-500">
                No categories configured yet — the default expense account will be used.
              </p>
            ) : null}
          </div>
          <Input
            label="Description (optional)"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder="What was this for?"
          />
          <Input
            label="Reference (optional)"
            value={form.referenceNumber}
            onChange={(e) => set('referenceNumber', e.target.value)}
            placeholder="Bill or receipt number"
          />
          <div>
            <label className="mb-1 block text-body-sm font-medium text-on-surface">
              Receipt / proof of expense {selectedCategory?.requiresReceipt ? '*' : '(optional)'}
            </label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={uploadReceipt.isPending}
                onClick={() => receiptFileRef.current?.click()}
              >
                <Camera className="h-4 w-4" /> Take photo / upload
              </Button>
              {form.receiptUrl && (
                <a
                  href={form.receiptUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 truncate text-xs font-medium text-primary underline"
                >
                  <UploadIcon className="h-3 w-3 shrink-0" /> View attached receipt
                </a>
              )}
            </div>
            {/* capture="environment" opens the phone's rear camera directly
                on mobile instead of just a file picker — the point of this
                fix is a field worker photographing a receipt on the spot,
                not hunting for an already-hosted URL. */}
            <input
              ref={receiptFileRef}
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              className="hidden"
              onChange={onReceiptFile}
            />
            <p className="mt-1 text-xs text-slate-500">
              {selectedCategory?.requiresReceipt
                ? `A receipt photo or reference number is required for "${selectedCategory.name}"`
                : 'Photograph or upload the bill/receipt — or paste a URL below if you already have one.'}
            </p>
            <Input
              value={form.receiptUrl}
              onChange={(e) => set('receiptUrl', e.target.value)}
              placeholder="https://example.com/receipt.pdf"
              className="mt-1.5"
            />
          </div>
        </section>

        {/* Amounts */}
        <section className="grid gap-3 sm:grid-cols-3">
          <Input
            type="number"
            step="0.01"
            min="0"
            label={form.taxTreatment === 'inclusive' ? 'Amount (tax inclusive)' : 'Amount (before tax)'}
            value={form.amount}
            onChange={(e) => set('amount', e.target.value)}
            required
          />
          <Input
            type="number"
            step="0.01"
            min="0"
            label="Discount (optional)"
            value={form.discount}
            onChange={(e) => set('discount', e.target.value)}
          />
          <Input
            label="Currency"
            value={form.currency}
            onChange={(e) => set('currency', e.target.value.toUpperCase().slice(0, 3))}
            maxLength={3}
          />
          <div>
            <Select
              label="Tax treatment"
              value={form.taxTreatment}
              onChange={(e) => set('taxTreatment', e.target.value as ExpenseTaxTreatment)}
              options={TAX_TREATMENTS.map((t) => ({ value: t.value, label: t.label }))}
            />
            {selectedTreatment ? <p className="mt-1 text-xs text-slate-500">{selectedTreatment.hint}</p> : null}
          </div>
          <Input
            type="number"
            step="0.01"
            min="0"
            max="100"
            label="Tax rate %"
            value={form.taxRate}
            onChange={(e) => set('taxRate', e.target.value)}
            disabled={form.taxTreatment === 'zero_rated' || form.taxTreatment === 'exempt'}
          />
          <Input
            type="number"
            step="0.01"
            min="0"
            max="100"
            label="Withholding % (optional)"
            value={form.withholdingRate}
            onChange={(e) => set('withholdingRate', e.target.value)}
            hint="Deducted from what the supplier receives"
          />
        </section>

        {/* Live totals */}
        <section className="rounded border border-slate-200 bg-slate-50 p-3">
          <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Net</p>
              <p className="font-medium">{formatMinor(totals.netMinor, form.currency)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Tax</p>
              <p className="font-medium">{formatMinor(totals.taxMinor, form.currency)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Gross</p>
              <p className="font-medium">{formatMinor(totals.grossMinor, form.currency)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Payable</p>
              <p className="font-medium">{formatMinor(totals.payableMinor, form.currency)}</p>
            </div>
          </div>
          {form.taxTreatment === 'reverse_charge' ? (
            <p className="mt-2 text-xs text-slate-500">
              Under reverse charge the supplier is paid the net amount; the tax is booked to both input and
              output tax so it nets to zero cash.
            </p>
          ) : null}
          <p className="mt-1 text-xs text-slate-400">Recalculated on the server when saved.</p>
        </section>

        {/* Funding */}
        <section className="grid gap-3 sm:grid-cols-2">
          <div>
            <Select
              label="How was this funded?"
              value={form.settlementMode}
              onChange={(e) => set('settlementMode', e.target.value as ExpenseSettlementMode)}
              options={SETTLEMENT_MODES.map((s) => ({ value: s.value, label: s.label }))}
            />
            {selectedSettlement ? (
              <p className="mt-1 font-mono text-xs text-slate-500">{selectedSettlement.hint}</p>
            ) : null}
          </div>
          <div />

          {contactRequirement === 'supplier' ? (
            <>
              <Input
                label="Supplier ID"
                value={form.supplierId}
                onChange={(e) => set('supplierId', e.target.value)}
                placeholder="CON-00001"
                required
                hint="Required: Accounts Payable is tracked per supplier"
              />
              <Input
                label="Supplier name (optional)"
                value={form.supplierName}
                onChange={(e) => set('supplierName', e.target.value)}
              />
            </>
          ) : null}

          {contactRequirement === 'employee' ? (
            <>
              <Input
                label="Employee ID"
                value={form.employeeId}
                onChange={(e) => set('employeeId', e.target.value)}
                placeholder="EMP-001"
                required
                hint="Required: Employee Payable is tracked per employee"
              />
              <Input
                label="Employee name (optional)"
                value={form.employeeName}
                onChange={(e) => set('employeeName', e.target.value)}
              />
              {/* Closes the loop: this is the one funding mode that feeds the
                  Reimbursements tab, and only once the expense is approved. */}
              <p className="text-xs text-slate-500 sm:col-span-2">
                Once approved, this appears under Expenses → Reimbursements as an outstanding
                balance for this employee, where a claim can be raised and paid.
              </p>
            </>
          ) : null}
        </section>

        {/* Allocation */}
        <section className="grid gap-3 sm:grid-cols-3">
          <Input label="Department (optional)" value={form.departmentId} onChange={(e) => set('departmentId', e.target.value)} />
          <Input label="Cost centre (optional)" value={form.costCenterId} onChange={(e) => set('costCenterId', e.target.value)} />
          <Input label="Campaign (optional)" value={form.campaignId} onChange={(e) => set('campaignId', e.target.value)} />
        </section>

        {/* Flags */}
        <section className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.isBillable}
              onChange={(e) => set('isBillable', e.target.checked)}
            />
            Billable to a customer
          </label>
          {form.isBillable ? (
            <Input
              type="number"
              step="0.01"
              min="0"
              label="Markup %"
              value={form.markupPercent}
              onChange={(e) => set('markupPercent', e.target.value)}
              className="max-w-[140px]"
            />
          ) : null}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.isReimbursable}
              onChange={(e) => set('isReimbursable', e.target.checked)}
            />
            Reimbursable (label only)
          </label>
        </section>

        {/*
          This flag is a classification: it is stored, filterable and shown as a
          chip, but no part of the reimbursement workflow reads it. Claims are
          driven entirely by the funding mode, because that is what credits
          Employee Payable. Ticking it while funding says anything else produced
          an expense that looks claimable and never appears on the Reimbursements
          tab, so say so here rather than leaving it to be discovered.
        */}
        {form.isReimbursable && form.settlementMode !== 'employee_paid' ? (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <p className="font-medium">This will not appear on the Reimbursements tab.</p>
            <p className="mt-0.5">
              &ldquo;Reimbursable&rdquo; is only a label. A claim can be raised only for an expense
              funded by an employee, because that is what credits Employee Payable.
              Set <span className="font-medium">How was this funded?</span> to
              &ldquo;Employee paid personally&rdquo; and enter the employee&rsquo;s ID.
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-1 h-auto px-0 py-0 text-xs underline"
              onClick={() => set('settlementMode', 'employee_paid')}
            >
              Switch funding to &ldquo;Employee paid personally&rdquo;
            </Button>
          </div>
        ) : null}

        <Input
          label="Notes (optional)"
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
          placeholder="Anything a reviewer should know"
        />

        {!isEdit ? (
          <p className="text-xs text-slate-500">
            Expenses always start as a draft and reach the ledger only after approval and posting.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

export default ExpenseFormModal;
